import React, { useState } from 'react';
import BillToLookupField, { type BillToValue } from '../../../components/BillToLookupField';
import DateTimePicker from '../../../components/DateTimePicker';
import SingleSelect from '../../../components/SingleSelect';
import { TanTable } from '../../../components/TanTable';
import service, { type SageDetailsResponse, type SageDetailsRow } from '../../../services/sage.service';
import { sageDetailsColumns } from './sageDetailsColumns';
import { calendarDate, dateParam, reportError, REPORT_BUTTON } from './reportUtils';

/**
 * Sage Export Details — reports_cms.pl::sagedetailsreport.
 *
 * Every line the AR export has written. Only that export writes `sageexportdetail`, so
 * driver pay, rebills and additional cost do not appear here.
 */
const SEARCH_BY = [
  { value: '', label: '(blank)' },
  { value: 'INVOICE #', label: 'Invoice #' },
  { value: 'GL CODE', label: 'GL Code' },
];

export default function SageExportDetailsReport() {
  const [start, setStart] = useState<Date | null>(null);
  const [end, setEnd] = useState<Date | null>(null);
  const [billto, setBillto] = useState<BillToValue>({ recno: 0, name: '' });
  const [searchby, setSearchby] = useState('');
  const [detaillist, setDetaillist] = useState('');
  const [includeLegacy, setIncludeLegacy] = useState(false);
  const [result, setResult] = useState<SageDetailsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    // Same two rules the backend enforces; checking here saves a round trip and says it
    // next to the controls the user has to fix.
    if (!(start && end) && !detaillist.trim()) {
      setError('Enter an invoice date range, or paste a list to search for.'); return;
    }
    if (detaillist.trim() && !searchby) {
      setError('Choose what the pasted list contains: Invoice # or GL Code.'); return;
    }
    if (start && end && start > end) { setError('End date must be on or after start date.'); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      setResult(await service.getExportDetails({
        ...(start ? { start: dateParam(start) } : {}),
        ...(end ? { end: dateParam(end) } : {}),
        ...(billto.recno ? { billto: billto.recno } : {}),
        ...(searchby ? { searchby: searchby as 'INVOICE #' | 'GL CODE' } : {}),
        ...(detaillist.trim() ? { detaillist } : {}),
        ...(includeLegacy ? { include_legacy: true } : {}),
      }));
    } catch (e) {
      setError(reportError(e, 'Unable to load Sage Export Details. Please try again.'));
    } finally { setLoading(false); }
  };

  return <div className="p-4 space-y-4 text-label">
    <h1 className="text-xl font-semibold">Sage Export Details</h1>
    <div className="rounded-lg border border-theme-border-primary bg-theme-bg-primary p-4 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-start">
        <DateTimePicker mode="date" labelType="custom" label="From Date" ariaLabel="From Date"
          value={start} onChange={v => setStart(calendarDate(v))} disabled={loading} />
        <DateTimePicker mode="date" labelType="custom" label="To Date" ariaLabel="To Date"
          value={end} onChange={v => setEnd(calendarDate(v))} disabled={loading} />
        <BillToLookupField value={billto} onChange={setBillto} disabled={loading} />
        <div>
          <div className="text-sm mb-1">Custom Search</div>
          <SingleSelect ariaLabel="Custom Search" options={SEARCH_BY} value={searchby}
            onChange={setSearchby} disabled={loading} />
        </div>
      </div>
      <div>
        <label htmlFor="sage-detaillist" className="text-sm mb-1 block">
          Invoice numbers or GL codes — one per line, or separated by spaces or commas
        </label>
        <textarea id="sage-detaillist" rows={5} value={detaillist} disabled={loading}
          onChange={e => setDetaillist(e.target.value.toUpperCase())}
          className="w-full rounded border border-theme-border-primary bg-theme-bg-secondary p-2 text-sm font-mono"
        />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={includeLegacy} disabled={loading}
          onChange={e => setIncludeLegacy(e.target.checked)} />
        Include pre-2022 MAS90 rows
        <span className="text-muted">
          — 6.2 million older rows, nothing newer than January 2022. Slower.
        </span>
      </label>
      <button className={REPORT_BUTTON} onClick={run} disabled={loading}>
        {loading ? 'Running…' : 'Run Report'}
      </button>
      {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
    </div>

    {result && <div className="space-y-2">
      <div className="text-sm text-muted">
        {result.rows.length.toLocaleString()} line{result.rows.length === 1 ? '' : 's'}
        {result.includedLegacy && ', including pre-2022 MAS90'}
        {result.truncated && (
          <span className="text-amber-600 dark:text-amber-400">
            {' '}— capped at 500,000. Narrow the dates or the list to see the rest.
          </span>
        )}
      </div>
      <TanTable<SageDetailsRow> settingsKey="accounting-sage-export-details"
        cardTitle="Sage Export Details" data={result.rows} columns={sageDetailsColumns}
        loading={loading} fillContainer enableColumnFilters />
    </div>}
  </div>;
}
